import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import VeteransAffairsTab from "./VeteransAffairsTab";
import VaSubmissionEvidenceModal from "./VaSubmissionEvidenceModal";
import { veteransAffairsStatusConfig, dueOnOptions } from "../../../utils/constants";
import FilterBar from "../../../pages/Home/FilterBar/FilterBar";
import PillDropdown from "../../../components/PillDropdown/PillDropdown";
import { 
  createHandleSelect, 
  createHandleFilterChange, 
  createHandleStateChange, 
  createOnSort,
  createHandlePageSizeChange
} from "../../../utils/commonHandlers";
import { useDispatch, useSelector } from "react-redux";
import {
  setFilters,
  setPageSizeForAll,
  fetchVeteransAffairsList,
  acknowledgeVaUploadErrorThunk,
} from "../../../slice/HomeSlice";
import { fetchVaUploadEvidence } from "../../../service/home";

// VA statuses arrive over the WebSocket. This is only the safety net for a
// dropped socket, which would otherwise leave the ledger silently stale.
const FALLBACK_REFRESH_MS = 60000;
const VA_SCOPE_OPTIONS = [
  { value: "mine", label: "My submissions" },
  { value: "matter", label: "My matters" },
  { value: "pod", label: "My pod" },
  { value: "firm", label: "Firm-wide" },
];

const readEvidenceError = (error) =>
  error?.response?.data?.detail ||
  error?.response?.data?.error ||
  error?.message ||
  "Unable to load VA submission evidence.";

const VeteransAffairsTabContainer = ({
  activeTab,
  handleTabChange,
  isLoading,
  isInitialLoad = false,
  veteransAffairsList,
  getRowProps,
  setError,
  setShowNotification,
  handleError,
  setIsLoading,
  handleAPICalls,
}) => {
  const dispatch = useDispatch();
  const { filterState } = useSelector((state) => state.home);
  const isSocketConnected = useSelector((state) => state.websocket?.isConnected);
  const vaStatusUpdatedAt = useSelector((state) => state.websocket?.vaStatusUpdatedAt);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [evidenceError, setEvidenceError] = useState("");
  const [evidenceData, setEvidenceData] = useState(null);
  const [acknowledgingId, setAcknowledgingId] = useState(null);
  const openUploadIdRef = useRef(null);
  const evidenceRequestRef = useRef(0);
  const lastHandledUpdateRef = useRef(vaStatusUpdatedAt);
  const socketStateRef = useRef(isSocketConnected);
  const catchUpPendingRef = useRef(false);
  const currentScope = filterState?.[activeTab]?.scope || "mine";
  const availableScopes = veteransAffairsList?.available_scopes || [
    "mine",
    "matter",
    "firm",
  ];

  // Create handlers using utility functions
  const handleSelect = createHandleSelect(dispatch, activeTab, setIsLoading, handleAPICalls, handleError);
  const handleFilterChange = createHandleFilterChange(dispatch, activeTab, setIsLoading, handleAPICalls, handleError);
  const handleStateChange = createHandleStateChange(dispatch, activeTab);
  const onSort = createOnSort(dispatch, filterState, activeTab, setIsLoading, handleAPICalls, handleError);
  const handlePageSizeChange = createHandlePageSizeChange(
    dispatch,
    activeTab,
    handleAPICalls,
    setPageSizeForAll
  );

  const getChipCount = (chip) => {
    return veteransAffairsList?.chip_counts?.[chip] || 0;
  };

  const handleAcknowledgeError = async (record) => {
    if (acknowledgingId) {
      return;
    }
    setAcknowledgingId(record.id);
    try {
      await dispatch(
        acknowledgeVaUploadErrorThunk({
          id: record.id,
          scope: currentScope,
          matterId: filterState?.[activeTab]?.matter_id,
        })
      ).unwrap();
    } catch (error) {
      handleError(error?.message || "Unable to acknowledge this error.");
    } finally {
      setAcknowledgingId(null);
    }
  };

  const loadEvidence = useCallback(async (
    uploadId,
    { silent = false, scope = currentScope } = {},
  ) => {
    // Requests are numbered because an open and a status refresh can be in
    // flight together, even for the same submission. Only the newest one may
    // write the record: what is on screen is read as evidence for a specific
    // submission, so it must never be an older version of it, and a failed
    // request must not blank out a newer one that already succeeded.
    const requestId = evidenceRequestRef.current + 1;
    evidenceRequestRef.current = requestId;
    const isNewest = () =>
      evidenceRequestRef.current === requestId && openUploadIdRef.current === uploadId;

    if (!silent) {
      setEvidenceLoading(true);
      setEvidenceError("");
      setEvidenceData(null);
    }
    try {
      const response = await fetchVaUploadEvidence(uploadId, scope);
      if (!isNewest()) {
        return;
      }
      setEvidenceData(response.data);
      setEvidenceError("");
    } catch (error) {
      // A background refresh that fails must leave the record the user is
      // already reading on screen; only an explicit open reports the error.
      if (!silent && isNewest()) {
        setEvidenceError(readEvidenceError(error));
      }
    } finally {
      // The spinner belongs to the explicit open that raised it, so it is
      // cleared when that request settles even if a refresh has overtaken it.
      if (!silent && openUploadIdRef.current === uploadId) {
        setEvidenceLoading(false);
      }
    }
  }, [currentScope]);

  const openEvidence = (record) => {
    openUploadIdRef.current = record.id;
    setEvidenceOpen(true);
    loadEvidence(record.id);
  };

  const closeEvidence = () => {
    setEvidenceOpen(false);
    openUploadIdRef.current = null;
  };

  // An open modal reads one submission from its own endpoint, so the list
  // refetch the WebSocket triggers does not reach it.
  useEffect(() => {
    if (vaStatusUpdatedAt === lastHandledUpdateRef.current) {
      return;
    }
    lastHandledUpdateRef.current = vaStatusUpdatedAt;
    if (openUploadIdRef.current) {
      loadEvidence(openUploadIdRef.current, { silent: true });
    }
  }, [vaStatusUpdatedAt, loadEvidence]);

  const refreshLedger = useCallback(() => {
    // Refreshing a hidden tab spends requests nobody is reading, so the work
    // is deferred until it is looked at again.
    if (document.visibilityState === "hidden") {
      catchUpPendingRef.current = true;
      return;
    }
    catchUpPendingRef.current = false;
    dispatch(fetchVeteransAffairsList());
    if (openUploadIdRef.current) {
      loadEvidence(openUploadIdRef.current, { silent: true });
    }
  }, [dispatch, loadEvidence]);

  // Either direction of a connection change earns one refresh. A socket that
  // comes back does not replay what it missed while it was gone, and a socket
  // that dies may have dropped the message it was carrying, so this is the
  // only point at which the ledger is known to be behind.
  useEffect(() => {
    if (activeTab !== "veteransAffairs" || socketStateRef.current === isSocketConnected) {
      return;
    }
    socketStateRef.current = isSocketConnected;
    refreshLedger();
  }, [activeTab, isSocketConnected, refreshLedger]);

  useEffect(() => {
    if (activeTab !== "veteransAffairs") {
      return undefined;
    }

    const refreshWhenVisible = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      if (catchUpPendingRef.current || !isSocketConnected) {
        refreshLedger();
      }
    };

    // Nothing is pushed while the socket is down, so the ledger is polled
    // instead. A live socket needs no polling on top of it.
    const timer = isSocketConnected ? null : setInterval(refreshLedger, FALLBACK_REFRESH_MS);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      if (timer) {
        clearInterval(timer);
      }
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [activeTab, isSocketConnected, refreshLedger]);

  const evidenceRowProps = (record) => ({
    ...(getRowProps ? getRowProps(record) : {}),
    onClick: (event) => {
      event.stopPropagation();
      openEvidence(record);
    },
  });

  const currentPage = filterState?.[activeTab]?.page || 1;
  // A "Pod" is a principal attorney's team. The options are the principal
  // attorneys who have at least one VA filing (server-supplied).
  const podOptions = (veteransAffairsList?.principal_attorneys || []).map(
    (attorney) => ({
      value: attorney.id,
      label: attorney.name,
    }),
  );
  const currentPod = filterState?.[activeTab]?.attorney || "";
  const changePod = (attorneyId) => {
    // Picking a pod means "every filing on that attorney's matters", so the
    // view widens to Firm-wide when the caller is allowed to see it;
    // otherwise the pick narrows whatever scope they already have.
    const widenToFirm =
      Boolean(attorneyId) &&
      currentScope !== "firm" &&
      availableScopes.includes("firm");
    const filters = { attorney: attorneyId, page: 1 };
    if (widenToFirm) {
      filters.scope = "firm";
    }
    dispatch(setFilters({ contextKey: activeTab, filters }));
    closeEvidence();
    dispatch(fetchVeteransAffairsList());
  };
  const changeScope = (scope) => {
    if (scope === currentScope) {
      return;
    }
    // Scope is a server-authorized predicate. Resetting pagination avoids a
    // stale page number pointing beyond the newly selected result set.
    dispatch(setFilters({
      contextKey: activeTab,
      filters: { scope, page: 1 },
    }));
    closeEvidence();
    dispatch(fetchVeteransAffairsList());
  };

  return (
    <>
      <div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 8,
            marginBottom: 12,
          }}
        >
          <div
            role="tablist"
            aria-label="VA submission visibility"
            style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
          >
            {VA_SCOPE_OPTIONS
              .filter(({ value }) => availableScopes.includes(value))
              .map(({ value, label }) => (
                <button
                  type="button"
                  key={value}
                  role="tab"
                  aria-selected={currentScope === value}
                  onClick={() => changeScope(value)}
                  style={{
                    border: 0,
                    borderRadius: 16,
                    cursor: "pointer",
                    padding: "6px 12px",
                    background: currentScope === value ? "#1D4ED8" : "#E5E7EB",
                    color: currentScope === value ? "#FFFFFF" : "#1F2937",
                  }}
                >
                  {label}
                </button>
              ))}
          </div>
          <PillDropdown
            key={activeTab + "-pod"}
            handleChange={changePod}
            value={currentPod}
            options={podOptions}
            placeholder="Pod"
            testId="va-pod-filter"
          />
        </div>
        <FilterBar
          activeTab={activeTab}
          handleSelect={handleSelect}
          isLoading={isLoading}
          handleFilterChange={handleFilterChange}
          handleStateChange={handleStateChange}
          filterState={filterState}
          statusOptions={veteransAffairsStatusConfig}
          dueOnOptions={dueOnOptions}
          getChipCount={getChipCount}
          handleChangePageSize={handlePageSizeChange}
        />
      </div>
      <VeteransAffairsTab
        currentPage={currentPage}
        getRowProps={evidenceRowProps}
        handlePageChange={handleFilterChange}
        onSort={onSort}
        veteransAffairsList={veteransAffairsList}
        filterState={filterState}
        isLoading={isLoading}
        isInitialLoad={isInitialLoad}
        onAcknowledgeError={handleAcknowledgeError}
        acknowledgingId={acknowledgingId}
      />
      <VaSubmissionEvidenceModal
        open={evidenceOpen}
        onClose={closeEvidence}
        loading={evidenceLoading}
        error={evidenceError}
        evidenceData={evidenceData}
      />
    </>
  );
};

VeteransAffairsTabContainer.propTypes = {
  activeTab: PropTypes.string.isRequired,
  handleTabChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isInitialLoad: PropTypes.bool,
  veteransAffairsList: PropTypes.object.isRequired,
  getRowProps: PropTypes.func,
  setError: PropTypes.func.isRequired,
  setShowNotification: PropTypes.func.isRequired,
  handleError: PropTypes.func.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  handleAPICalls: PropTypes.func.isRequired,
};

export default VeteransAffairsTabContainer; 